import type { ChatMessage } from '../hooks/use-chat-ws';

// The Task subagent_type values that get a journey progress card. Other subagents'
// frames are attributed on the wire too but keep rendering inline.
export const JOURNEY_SUBAGENT_TYPES = new Set([
  'agent-builder',
  'playbook-builder',
  'entity-investigator',
  'entity-data-ops',
  'agent-runner',
]);

// The built-in subagent-spawn tool: current CLIs emit "Agent", older ones "Task".
export const SUBAGENT_SPAWN_TOOLS = new Set(['Task', 'Agent']);

const JOURNEY_LABELS: Record<string, { label: string; running: string }> = {
  'agent-builder': { label: 'Agent builder', running: 'Building the agent' },
  'playbook-builder': { label: 'Playbook builder', running: 'Wiring the playbook' },
  'entity-investigator': { label: 'Investigator', running: 'Investigating' },
  'entity-data-ops': { label: 'Data operations', running: 'Updating data' },
  'agent-runner': { label: 'Agent runner', running: 'Running the agent' },
};

// general-purpose is Claude's built-in delegate; label it "Research" rather than leaking the raw
// subagent_type into the chat. Any other non-journey subagent falls back to its prettified type.
const DELEGATE_LABELS: Record<string, string> = {
  'general-purpose': 'Research',
};

export const journeyDisplay = (agent: string): { label: string; running: string } => {
  if (JOURNEY_LABELS[agent]) return JOURNEY_LABELS[agent];
  const curated = DELEGATE_LABELS[agent];
  return { label: curated ?? prettifySubagentType(agent), running: 'Working' };
};

export const prettifySubagentType = (agent: string): string => {
  if (!agent) return 'Specialist';
  const words = agent.replace(/[-_]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * The spawning Task tool_use id this frame belongs to, or '' for main-thread frames.
 * Reads defensively: the generated types claim the field is required, but frames
 * persisted before attribution existed simply lack it.
 */
export const getParentToolUseId = (msg: ChatMessage): string => {
  const direct = (msg as { parent_tool_use_id?: unknown }).parent_tool_use_id;
  if (typeof direct === 'string' && direct) return direct;
  if (msg.type === 'task_progress') {
    const fromData = (msg.data as { tool_use_id?: unknown } | undefined)?.tool_use_id;
    if (typeof fromData === 'string' && fromData) return fromData;
  }
  return '';
};

/** The subagent name stamped on the frame ('' when absent/main-thread). */
export const getFrameAgent = (msg: ChatMessage): string => {
  const agent = (msg as { agent?: unknown }).agent;
  return typeof agent === 'string' ? agent : '';
};

export const getSpawnSubagentType = (msg: ChatMessage): string => {
  if (msg.type !== 'tool_use' || !SUBAGENT_SPAWN_TOOLS.has(msg.tool)) return '';
  const subagentType = (msg.input as { subagent_type?: unknown } | undefined)?.subagent_type;
  return typeof subagentType === 'string' ? subagentType : '';
};

/** Any built-in delegate spawn (Task/Agent), regardless of subagent type. */
export const isSubagentSpawn = (msg: ChatMessage): boolean => {
  return msg.type === 'tool_use' && SUBAGENT_SPAWN_TOOLS.has(msg.tool);
};

export const isJourneySpawn = (msg: ChatMessage): boolean => {
  return JOURNEY_SUBAGENT_TYPES.has(getSpawnSubagentType(msg));
};

/** A journey subagent runs as a background task (pinned above the composer while live);
 * every other delegated subagent renders inline in place. */
export const isJourneyAgent = (agent: string): boolean => {
  return JOURNEY_SUBAGENT_TYPES.has(agent);
};

const TERMINAL_TASK_STATUSES = new Set(['completed', 'failed', 'stopped', 'killed']);
const ERROR_TASK_STATUSES = new Set(['failed', 'stopped', 'killed']);

export const getTaskStatus = (msg: ChatMessage): string => {
  if (msg.type !== 'task_progress') return '';
  const status = (msg as { status?: unknown }).status;
  return typeof status === 'string' ? status : '';
};

export const isTerminalTaskStatus = (status: string): boolean => TERMINAL_TASK_STATUSES.has(status);

export const isErrorTaskStatus = (status: string): boolean => ERROR_TASK_STATUSES.has(status);

/** The journey's latest known lifecycle status ('' before the first status frame lands). */
export const journeyGroupStatus = (messages: ChatMessage[]): string => {
  for (let i = messages.length - 1; i >= 0; i--) {
    const status = getTaskStatus(messages[i]);
    if (status) return status;
  }
  return '';
};
